import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/AuthContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Sun, X } from 'lucide-react';
import { SwipeablePrompt } from '../../components/user/SwipeablePrompt';
import { updateStreak } from '../../utils/streakTracker';
import { checkAchievements } from '../../components/user/AchievementSystem';

const morningPrompts = [
  {
    id: 'gratitude',
    question: '3 Things I\'m Grateful For',
    description: 'Don\'t just write them down. Pause and really reflect on them for a few moments.',
    placeholder: 'e.g., My family, my health, my team...'
  },
  {
    id: 'mini_goals',
    question: '3 Mini Goals for Making Today Great',
    description: 'Be specific, and make sure they\'re easily attainable and point you in the direction of your big goals. Little wins stack on top of each other to create big wins.',
    placeholder: 'e.g., Stay focused, help my team, stay safe...'
  },
  {
    id: 'intention',
    question: 'Today\'s Intention',
    description: 'What mindset will you bring into your interactions with the world today?',
    placeholder: 'e.g., Stay present, be patient, lead with compassion...'
  }
];

export const MorningJournalNew: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addEvent } = useAnalytics();
  
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [overallMood, setOverallMood] = useState(7);
  const [showMoodSlider, setShowMoodSlider] = useState(true);
  const [loading, setLoading] = useState(false);
  const [startTime] = useState(new Date());

  const handleResponseChange = (promptId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [promptId]: value
    }));
  };

  const handleComplete = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const timeToComplete = Math.round((new Date().getTime() - startTime.getTime()) / 1000);
      
      // Build reflection details
      const reflectionDetails = morningPrompts.map(prompt => ({
        question: prompt.question,
        answer: responses[prompt.id] || ''
      }));

      // Convert responses to the old format for compatibility
      const formattedResponses = morningPrompts.map(prompt => ({
        questionId: prompt.id,
        value: responses[prompt.id] || '',
        timestamp: new Date()
      }));

      // Save to Firestore
      const journalEntry = {
        uid: user.uid,
        promptId: 'morning_default',
        promptType: 'morning',
        responses: formattedResponses,
        overallMood,
        reflectionDetails,
        completedAt: new Date(),
        timeToComplete,
        sharedWithDepartment: false,
        sharedWithTherapist: false,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'users', user.uid, 'promptedJournals'), journalEntry);

      // Update streak and check for achievements
      await updateStreak(user.uid, 'journal');
      await checkAchievements(user, false, undefined, undefined);

      // Track analytics
      await addEvent('journal.create', { 
        moduleId: 'morning_checkin',
        durationMs: timeToComplete * 1000 
      });

      // Navigate to results
      navigate('/journal/morning/results', { 
        state: { 
          mood: overallMood,
          responses: formattedResponses.length,
          reflectionDetails,
          promptType: 'morning'
        }
      });

    } catch (error) {
      console.error('Error submitting morning journal:', error);
      alert('Error saving your check-in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="bg-gradient-to-br from-yellow-500 to-orange-500 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto shadow-2xl animate-pulse mb-6">
            <Sun className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Saving Your Reflection</h2>
          <p className="text-gray-600">Just a moment...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 p-4 sm:p-6 md:p-8"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}
    >
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/journal')}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
          <div className="text-sm text-gray-600">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>

        {/* Title Section */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="bg-gradient-to-br from-yellow-500 to-orange-500 w-16 h-16 sm:w-20 sm:h-20 rounded-3xl flex items-center justify-center mx-auto shadow-xl mb-4">
            <Sun className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Morning Check-In
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            Prime your brain to focus on the good stuff
          </p>
        </div>

        {/* Mood Slider */}
        {showMoodSlider ? (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 mb-8 max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                How are you feeling this morning?
              </h2>
              <p className="text-gray-600">
                Rate your current mood from 1 to 10
              </p>
            </div>

            {/* Mood Display */}
            <div className="text-center mb-6">
              <div className="text-6xl mb-2">
                {overallMood >= 9 ? '😊' : 
                 overallMood >= 7 ? '🙂' : 
                 overallMood >= 5 ? '😐' : 
                 overallMood >= 3 ? '😟' : '😢'}
              </div>
              <div className="text-4xl font-bold text-gray-900">{overallMood}</div>
              <div className="text-sm text-gray-500 mt-1">
                {overallMood >= 9 ? 'Excellent' : 
                 overallMood >= 7 ? 'Good' : 
                 overallMood >= 5 ? 'Okay' : 
                 overallMood >= 3 ? 'Not Great' : 'Struggling'}
              </div>
            </div>

            {/* Slider */}
            <div className="mb-8">
              <input
                type="range"
                min="1"
                max="10"
                value={overallMood}
                onChange={(e) => setOverallMood(Number(e.target.value))}
                className="w-full h-3 bg-gradient-to-r from-red-300 via-yellow-300 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                style={{
                  background: `linear-gradient(to right, 
                    rgb(252, 165, 165) 0%, 
                    rgb(254, 240, 138) 50%, 
                    rgb(134, 239, 172) 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>1</span>
                <span>5</span>
                <span>10</span>
              </div>
            </div>

            {/* Continue Button */}
            <button
              onClick={() => setShowMoodSlider(false)}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-xl active:scale-95 transition-all duration-200 shadow-lg"
            >
              Continue to Reflection
            </button>
          </div>
        ) : (
          <>
            {/* Swipeable Prompts */}
            <SwipeablePrompt
              prompts={morningPrompts}
              responses={responses}
              onResponseChange={handleResponseChange}
              onComplete={handleComplete}
              accentColor="yellow"
              currentIndex={currentIndex}
              onIndexChange={setCurrentIndex}
            />
          </>
        )}

        {/* Quote or Encouragement */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 italic">
            "Each morning we can start again. What we do today matters most."
          </p>
        </div>
      </div>
    </div>
  );
};

